import { isRedirect, redirect } from '@tanstack/react-router'
import type { AxiosError } from 'axios'
import axios from 'axios'
import { z } from 'zod'

import { logSentry } from '@gto/sentry'
import { sessionStore } from '@gto/session'

import {
  clearOAuthStorage,
  getOAuthClientId,
  getOAuthRedirectUri,
  getOAuthTokenUrl,
  getProcessedCodeKey,
  getStoredCodeVerifier,
  getStoredFrom,
  getStoredState,
  timingSafeEqual,
} from '@/utils/oauth'

type OAuthTokenResponse = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  scope: string
}

// `from` is carried alongside the error so the retry handler can re-seed the
// next OAuth flow with the same deep-link target. clearOAuthStorage wipes
// SESSION_KEY_FROM, so we capture it before clearing and hand it back.
export type LoaderResult = {
  status: 'error'
  message: string
  from?: string
}

// django-oauth-toolkit returns `{error, error_description}` (RFC 6749 §5.2);
// our DRF wrappers add `{non_field_errors, detail, message}` shapes.
const ErrorResponseSchema = z.object({
  non_field_errors: z.array(z.string()).optional(),
  detail: z.string().optional(),
  message: z.string().optional(),
  error_description: z.string().optional(),
  error: z.string().optional(),
})

function extractErrorMessage(error: AxiosError): string {
  const parsed = ErrorResponseSchema.safeParse(error.response?.data)
  if (parsed.success) {
    const data = parsed.data

    if (data.non_field_errors !== undefined && data.non_field_errors.length > 0) {
      return data.non_field_errors.join(' ')
    }

    // error_description is human-readable; error is the machine code
    // ("invalid_grant"), shown only as a last resort.
    const simple = data.detail ?? data.message ?? data.error_description ?? data.error ?? ''

    if (simple.length > 0) {
      return simple
    }
  }

  return error.message
}

export async function runOAuthCallbackLoader(search: {
  code?: string
  state?: string
}): Promise<LoaderResult> {
  // Snapshot the deep-link target before any clearOAuthStorage call wipes it,
  // so the error result can hand it back to the retry handler.
  const from = getStoredFrom() ?? undefined

  if (search.code === undefined || search.code.length === 0) {
    clearOAuthStorage()

    return { status: 'error', message: '', from }
  }

  const savedState = getStoredState()
  if (
    savedState === null ||
    search.state === undefined ||
    !timingSafeEqual(savedState, search.state)
  ) {
    clearOAuthStorage()

    return {
      status: 'error',
      message: 'State mismatch — possible CSRF attempt. Please try again.',
      from,
    }
  }

  const codeVerifier = getStoredCodeVerifier()
  if (codeVerifier === null) {
    clearOAuthStorage()

    return { status: 'error', message: 'Missing code verifier. Please try again.', from }
  }

  const successTarget = from ?? '/'

  // BE invalidates the code after first use; re-running the loader on the
  // same code (StrictMode, fast back-nav, callback refresh) would 400.
  const processedCodeKey = getProcessedCodeKey(search.code)
  if (sessionStorage.getItem(processedCodeKey) !== null) {
    throw redirect({ to: successTarget, replace: true })
  }
  sessionStorage.setItem(processedCodeKey, 'true')

  try {
    // Bare axios — bypass `axiosInstance` so the request interceptor doesn't
    // try to inject a stale Bearer token or refresh-loop on this pre-session
    // POST. The token endpoint is unauthenticated by definition.
    const result = await axios.post<OAuthTokenResponse>(getOAuthTokenUrl(), {
      grant_type: 'authorization_code',
      code: search.code,
      code_verifier: codeVerifier,
      client_id: getOAuthClientId(),
      redirect_uri: getOAuthRedirectUri(),
    })

    clearOAuthStorage()
    sessionStore.getState().setAccessToken(result.data.access_token)
    localStorage.setItem('user_refresh', result.data.refresh_token)

    throw redirect({ to: successTarget, replace: true })
  } catch (error) {
    // TanStack's redirect() throws a tagged Response — re-throw so the router
    // navigates instead of treating it as a token-exchange failure.
    if (isRedirect(error)) {
      throw error
    }

    logSentry('admin-web: OAuth token exchange failed', error)
    clearOAuthStorage()

    return {
      status: 'error',
      message: extractErrorMessage(error as AxiosError),
      from,
    }
  }
}
